import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal, Button } from 'antd';
import { PlusOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import DeleteModal from '../../shared/DeleteModal';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { ParticipantScoreDto, SetRow, SetScore } from '../../../types/types';
import { isValidNumber, parseScoreData } from '../../../utils/eventUtils';
import { TournamentTypeString } from '../../../enums/enums';

type Props = {
  visible: boolean;
  onClose: () => void;
  participant1: ParticipantScoreDto | undefined;
  participant2: ParticipantScoreDto | undefined;
  tournamentType: string;
  sportName: string;
  disabled: boolean;
  tournamentMatchId: number;
  onSave: (p1Sets: SetScore[], p2Sets: SetScore[]) => void;
};

const UpdateScoreModal: React.FC<Props> = ({
  visible,
  onClose,
  participant1,
  participant2,
  sportName,
  onSave,
  disabled,
  tournamentType,
}) => {
  const snackbar = useSnackbar();
  const [scoreRows, setScoreRows] = useState<SetRow[]>([]);
  const [deleteSetId, setDeleteSetId] = useState<number | null>(null);
  const [isDeleteScoreModalOpen, setIsDeleteScoreModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    const p1Score: any = participant1?.scoreData
      ? parseScoreData(participant1?.scoreData, sportName)
      : null;

    const p2Score: any = participant2?.scoreData
      ? parseScoreData(participant2?.scoreData, sportName)
      : null;

    const longest = Math.max(
      p1Score?.sets?.length || 0,
      p1Score?.sets?.length || 0,
    );
    const merged: SetRow[] = Array.from({ length: longest }).map((_, i) => ({
      id: i,
      p1: p1Score?.sets?.[i]?.toString() ?? '',
      p2: p2Score?.sets?.[i]?.toString() ?? '',
    }));
    setScoreRows(merged.length > 0 ? merged : [{ id: 1, p1: '', p2: '' }]);
  }, [participant1, participant2, visible, sportName]);

  const addRow = () => {
    const nextId =
      scoreRows.length > 0
        ? Math.max(...scoreRows.map((s) => Number(s.id))) + 1
        : 1;

    setScoreRows((prev) => [...prev, { id: nextId, p1: '', p2: '' }]);
  };

  const deleteRow = (id: number) => {
    setScoreRows((prev) => prev.filter((r) => r.id !== id));
    setDeleteSetId(null);
    setIsDeleteScoreModalOpen(false);
  };

  const updateValue = (id: number, key: 'p1' | 'p2', value: string) => {
    setScoreRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)),
    );
  };

  /* -------- Validation (RN parity) -------- */
  const isFormValid = useMemo(
    () =>
      scoreRows.length > 0 &&
      scoreRows.every((r) => r.p1.trim() !== '' && r.p2.trim() !== '') &&
      scoreRows.every((r) => isValidNumber(r.p1) && isValidNumber(r.p2)),
    [scoreRows],
  );

  const handleSave = () => {
    if (!isFormValid) {
      return snackbar.error('Please enter valid score.');
    }
    const p1Sets: SetScore[] = scoreRows.map((r) => ({
      id: r.id,
      value: r.p1,
    }));

    const p2Sets: SetScore[] = scoreRows.map((r) => ({
      id: r.id,
      value: r.p2,
    }));

    onSave(p1Sets, p2Sets);
  };

  const getTeamName = (participant: ParticipantScoreDto | undefined) => {
    const p1 = participant?.participantName?.trim();
    const p2 = participant?.partnerName?.trim();
    const showPartner =
      tournamentType !== TournamentTypeString.Singles &&
      tournamentType !== TournamentTypeString.Team;

    if (p1 && p2 && showPartner) return `${p1} + ${p2}`;
    return p1 || p2 || 'N/A';
  };

  return (
    <>
      <Modal open={visible} footer={null} onCancel={onClose} centered>
        <Title>Update Score</Title>
        <AddSetButton
          icon={<PlusOutlined color={COLORS.skyBlue.primary} />}
          onClick={addRow}
        >
          Add set
        </AddSetButton>
        <Section>
          <PlayerName>{getTeamName(participant1)}</PlayerName>
          <RowScroller>
            {scoreRows.map((r, i) => (
              <SetBox key={r.id}>
                <StyledLabel>Set {i + 1}</StyledLabel>
                <InputWrap>
                  <input
                    value={r.p1}
                    onChange={(e) => updateValue(r.id, 'p1', e.target.value)}
                    placeholder="0"
                  />
                  {i > 0 && (
                    <CloseCircleFilled
                      onClick={() => {
                        setDeleteSetId(r.id);
                        setIsDeleteScoreModalOpen(true);
                      }}
                    />
                  )}
                </InputWrap>
              </SetBox>
            ))}
          </RowScroller>
        </Section>
        <Section>
          <PlayerName>{getTeamName(participant2)}</PlayerName>
          <RowScroller>
            {scoreRows.map((r, i) => (
              <SetBox key={r.id}>
                <StyledLabel>Set {i + 1}</StyledLabel>
                <InputWrap>
                  <input
                    value={r.p2}
                    onChange={(e) => updateValue(r.id, 'p2', e.target.value)}
                    placeholder="0"
                  />
                  {i > 0 && (
                    <CloseCircleFilled
                      onClick={() => {
                        setDeleteSetId(r.id);
                        setIsDeleteScoreModalOpen(true);
                      }}
                    />
                  )}
                </InputWrap>
              </SetBox>
            ))}
          </RowScroller>
        </Section>
        <Footer>
          <Button onClick={onClose} disabled={disabled}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!isFormValid || disabled}
          >
            Save score
          </Button>
        </Footer>
      </Modal>
      <DeleteModal
        open={isDeleteScoreModalOpen}
        onCancel={() => {
          setDeleteSetId(null);
          setIsDeleteScoreModalOpen(false);
        }}
        onConfirm={() => deleteSetId && deleteRow(deleteSetId)}
        moduleType="Set"
        body="set? All scores for both players will be permanently removed."
      />
    </>
  );
};

export default UpdateScoreModal;

/* ================= styles ================= */

const AddSetButton = styled(Button)`
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${COLORS.skyBlue.primary};
  background-color: ${COLORS.white.primary};
  box-shadow: none;
  color: ${COLORS.grey.medium} !important
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
   
  &:hover,
  &:focus,
  &:active {
    background-color: ${COLORS.white.primary} !important;
    border-color: ${COLORS.skyBlue.primary} !important;
   color: ${COLORS.grey.medium} !important
    box-shadow: none !important;
  }

  &.ant-btn:hover,
  &.ant-btn:focus {
    background-color: ${COLORS.white.primary} !important;
  }

  svg {
    color: ${COLORS.skyBlue.primary};
  }
`;

const Title = styled.h3`
  margin-bottom: 12px;
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const Section = styled.div`
  margin-top: 16px;
`;

const PlayerName = styled.div`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: 6px;
`;

const RowScroller = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const SetBox = styled.div`
  width: 64px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
`;

const StyledLabel = styled.label`
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.grey.dark};
`;

const InputWrap = styled.div`
  position: relative;
  width: 56px;

  input {
    width: 100%;
    height: 36px;
    padding: 6px;
    border-radius: 8px;
    border: 1px solid ${COLORS.grey.secondaryLight};
    font-size: ${TEXT_SIZES.SM}px;
    text-align: center;
    outline: none;
    box-shadow: none;
    background-color: ${COLORS.white.primary};

    &:hover,
    &:focus,
    &:active {
      border-color: ${COLORS.grey.secondaryLight};
      outline: none;
      box-shadow: none;
    }
  }

  svg {
    position: absolute;
    top: -4px;
    right: -4px;
    background: ${COLORS.white.primary};
    border-radius: 50%;
    cursor: pointer;
    color: ${COLORS.red.primary};
    font-size: ${TEXT_SIZES.SM}px;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`;
